import React from 'react'
import Link, { withPrefix } from 'gatsby-link'
import 'babel-polyfill'

import Map from '../components/Visualizations/containers/map'
import Performance from '../components/Visualizations/containers/performance'
import Button from '../components/Button/Button.js'
import ShareExhibit from '../components/ShareExhibit/ShareExhibit.js'
import caseStudyNewYorkImageUrl, { ReactComponent as CaseStudyNewYorkImage } from '../assets/svg/case-study-new-york.svg'
import caseStudyTwoStatesImageUrl, { ReactComponent as CaseStudyTwoStatesImage } from '../assets/svg/case-study-two-states.svg'
import caseStudyMortalityImageUrl, { ReactComponent as CaseStudyMortalityImage } from '../assets/svg/case-study-hospital-mortality.svg'

import img from "../assets/img/scorecard_rankings.png"
import exhibitOverallImg from "../assets/img/exhibit-overall.png"

const Rankings = ({ data }) => (
  <div>
    <article>
      <div className="section highlights_header section__heavy">
        <figure className="highlights_header__image">
          <img src={ img } alt=""/>
        </figure>
        <div className="highlights_header__content">
          <h1 className="title__page">State Rankings</h1>
          <p>Hawaii, Massachusetts, Minnesota, Connecticut, and Washington are the top-ranked states according to the <em>2019 Scorecard</em>, which assesses all 50 states and the District of Columbia on more than 45 measures of access to health care, quality of care, service use and costs of care, health outcomes, and income-based health care disparities.</p>
        </div>
      </div>
      <div className="section section__centered section__narrow">
          <div className="section__content">
            <h2>Which states moved up the most in the rankings between 2013 and 2017, and which states dropped?</h2>
            <p>California had the largest jump in rankings, up 12 spots. New York and Washington each moved up 10 spots in the rankings, and Rhode Island rose nine spots.</p>
            <p>Delaware fell 17 spots in the rankings, while Wyoming, Virginia, and Maryland fell 11, nine, and seven spots, respectively.</p>
          </div>
      </div>
      <div className="section l-full">
        <div id="exhibit-14" className="section__row">
          <div id="overall-health-system-performance" className="section__content section__exhibit section__exhibit__padded section__exhibit__interactive">
            <figure>
              <h2 className="title__block">Overall health system performance varies greatly among states</h2>
              <div className="instructions">
                <ul>
                  <li>Adjust the options to see how states performed when compared to the baseline, against other states in their region, and in different dimensions.</li>
                  <li>Highlight a state by selecting its circle</li>
                </ul>
              </div>
              <Performance />
              <figcaption>
                <p className="performance__large">States are arranged in rank order from left (best) to right (worst), based on their <em>2019 Scorecard</em> rank.</p>
                <p className="performance__small">States are arranged in rank order from top (best) to bottom (worst), based on their <em>2019 Scorecard</em> rank.</p>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
      <hr className="section__divider"/>
      <div className="section section__centered">
        <h2 className="section__title">What are the leading states by region?</h2>
        <div className="section__row">
          <div className="section__content">
            <ul className="list__4-up">
              <li><strong>Great Lakes</strong><br /> Wisconsin</li>
              <li><strong>Mid-Atlantic</strong><br /> New York</li>
              <li><strong>New England</strong><br /> Massachusetts</li>
              <li><strong>Plains</strong><br /> Minnesota</li>
              <li><strong>Rocky Mountain</strong><br /> Colorado</li>
              <li><strong>Southeast</strong><br /> Virginia</li>
              <li><strong>Southwest</strong><br /> Arizona</li>
              <li><strong>West</strong><br /> Hawaii</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="section l-full">
        <div className="section__row">
          <div id="exhibit-16" className="section__content section__exhibit section__exhibit__interactive section__exhibit__padded">
            <figure>
              <h2 className="title__block">State health system performance varies within regions</h2> 
              <div className="instructions">
                <ul>
                  <li>Select a state to highlight a region of the country.</li>
                  <li>Choose a selected state to return to the national view.</li>
                </ul>
              </div>
              <Map />
              <figcaption>
                <p>Note: Regions are U.S. Census regions. Regional shading is based on performance among states within the region only. See Scorecard Methods for additional detail.</p>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
      <hr className="section__divider"/>
      <div className="section section__centered section__narrow">
        <div className="section__content">
          <h2>Which states improved on the most indicators?</h2>
          <p>Rhode Island improved on 21 of 45 indicators we track over time, the most of any state. Missouri, Washington, and West Virginia each improved on 19 indicators.</p>
          <p>Delaware, Hawaii, New Mexico, and Wyoming each got worse on more indicators than they improved on.</p>
        </div>
      </div>
      <div className="section section__full">
        <div className="section__row">
          <div id="exhibit-15" className="section__content section__exhibit section__exhibit__padded">
            <figure>
              <h2 className="title__block">More improvement than decline</h2> 
              <img src={ withPrefix(`/img/exhibits/exhibit-15-web.png`) } alt="" />
              <figcaption>
                <p>Notes: Based on five-year trends for 45 of 47 total indicators (Disparity dimension not included); trend data are not available for all indicators. Bar length equals the total number of indicators with any improvement or worsening with an absolute value greater than 0.5 standard deviations (StDev) of the state distribution.</p>
              </figcaption>
            </figure>
            <ShareExhibit
              exhibit="exhibit-15"
              site={data.site.siteMetadata}
              social={ data.allSocialJson.edges} />
          </div>
        </div>
      </div>
      <div className="section section__centered section__narrow">
        <div className="section__content">
          <h2>What’s the trend?</h2>
          <p>On balance, the <em>2019 State Scorecard</em> finds more improvement than decline between 2013 and 2017, but trends are mixed. Almost all states improved on more indicators than they worsened, but no state improved on a majority of indicators. Most states (42) saw little or no change on more indicators than they improved on.</p>
        </div>
      </div>
    </article>
  </div>
)

export default Rankings

export const query = graphql`
  query RankingsQuery {
    site {
      pathPrefix
      siteMetadata {
        title
        year
        subtitle
        siteUrl
        siteDescription
      }
    }
    allSocialJson {
      edges {
        node {
          page_id
          path
          title
          share_path
          exhibit
        }
      }
    }
  }
`


// WEBPACK FOOTER //
// ./src/pages/rankings.js